import { useRef, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { AbsenceCategory, AbsenceCategorySchema, ScheduleConflictItem } from "@myapp/contract";
import PageLayout from "../../../shared/ui/layouts/PageLayout/PageLayout";
import { routes } from "../../../shared/config/routes";
import { useCanPlanAllAbsences } from "../../../shared/hooks/useUserRoles";
import { useCreateAbsence, getAbsenceCategoryLabel } from "../../../entities/absence";
import {
  TimeManagementFormWidget,
  toCreateDto,
  type TimeManagementFormValues,
} from "../../../features/time-management";
import { useSuccessDialog } from "../../../shared/hooks/useSuccessDialog";
import { SuccessDialog } from "../../../shared/ui/feedback/SuccessDialog/SuccessDialog";
import { ConflictDialog } from "../../../shared/ui/feedback/ConflictDialog/ConflictDialog";
import { BlockerDialog } from "../../../shared/ui/feedback/BlockerDialog/BlockerDialog";
import { parseConflictsFromError } from "../../../shared/lib/errors/parseConflictsFromError";
import { parseCompletedBlockersFromError } from "../../../shared/lib/errors/parseCompletedBlockersFromError";

export function TimeManagementCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canPlanAllCategories = useCanPlanAllAbsences();
  const createMutation = useCreateAbsence();
  const submitRef = useRef<(() => void) | null>(null);
  const { showSuccess, dialogProps } = useSuccessDialog();

  const [conflicts, setConflicts] = useState<ScheduleConflictItem[] | null>(null);
  const [blockers, setBlockers] = useState<ScheduleConflictItem[] | null>(null);
  const pendingValuesRef = useRef<TimeManagementFormValues | null>(null);

  const parsed = AbsenceCategorySchema.safeParse(searchParams.get("category"));
  const category: AbsenceCategory | null = parsed.success ? parsed.data : null;

  /**
   * Renders `<Navigate>` instead of calling `navigate()`, which used to happen here — that is a
   * side effect during render, and React warns about it.
   *
   * Two ways to be turned away. No usable `?category` leaves nothing to render a form from. And
   * without the planner roles there is exactly one category on offer — everyone else plans
   * Privatzeit and nothing else — so a hand-typed `?category=VACATION` goes back to the list rather
   * than opening a form the API would reject on submit.
   */
  if (!category || (!canPlanAllCategories && category !== "PERSONAL_LEAVE")) {
    return <Navigate to={routes.zeitmanagement.root} replace />;
  }

  const handleSubmit = async (values: TimeManagementFormValues, force = false) => {
    try {
      const dto = toCreateDto(values, force);
      await createMutation.mutateAsync(dto);
      showSuccess("Erfolgreich gespeichert", () => navigate(routes.zeitmanagement.root));
    } catch (err) {
      const blocking = parseCompletedBlockersFromError(err);
      if (blocking) {
        setBlockers(blocking);
        return;
      }
      const detected = parseConflictsFromError(err);
      if (detected) {
        pendingValuesRef.current = values;
        setConflicts(detected);
      } else {
        throw err;
      }
    }
  };

  const handleConflictConfirm = async () => {
    if (!pendingValuesRef.current) return;
    await handleSubmit(pendingValuesRef.current, true);
    setConflicts(null);
    pendingValuesRef.current = null;
  };

  const handleConflictCancel = () => {
    setConflicts(null);
    pendingValuesRef.current = null;
  };


  
  return (
    <>
      <PageLayout
        pageTitle={`${getAbsenceCategoryLabel(category)} planen`}
        scrollable
        back
        backTo={routes.zeitmanagement.root}
        primaryAction={{
          label: "Speichern",
          onClick: () => submitRef.current?.(),
          loading: createMutation.isPending,
        }}
      >
        <TimeManagementFormWidget
          category={category}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending}
          submitRef={submitRef}
        />
      </PageLayout>
      <SuccessDialog
        open={dialogProps.open}
        onOpenChange={(open) => { if (!open) dialogProps.onClose(); }}
        title={dialogProps.title}
        onClose={dialogProps.onClose}
      />
      <ConflictDialog
        open={conflicts !== null}
        conflicts={conflicts ?? []}
        onConfirm={handleConflictConfirm}
        onCancel={handleConflictCancel}
        isLoading={createMutation.isPending}
      />
      <BlockerDialog
        open={blockers !== null}
        blockers={blockers ?? []}
        onClose={() => setBlockers(null)}
        onGoToReschedule={() => navigate(routes.reschedule.root)}
      />
    </>
  );
}
